// Shared building blocks for the Naturals demo. Same layout patterns as the reference salon
// dashboard (page head, welcome bar, solid stat cards, chip filters, coloured tables, pagination,
// modals), in the Naturals purple and gold palette. Class names live in ../theme.css.
import React, { useEffect, useState } from 'react';
import { FaInstagram, FaWhatsapp, FaPhoneAlt, FaStore, FaCut } from 'react-icons/fa';
import { SALON_NAME } from '../services/api';
import { PortalSectionFallback } from '../../../shared/portal/PortalLoader';

// ── icons (inline, so sizes and colours follow the text) ─────────────────────

const svg = (children, size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

export const SearchIcon = ({ size = 15 }) => svg(<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>, size);
export const RefreshIcon = ({ size = 14 }) =>
  svg(<><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>, size);
export const PlusIcon = ({ size = 15 }) => svg(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>, size);
export const BackIcon = ({ size = 18 }) => svg(<polyline points="15 18 9 12 15 6" />, size);
export const CalendarIcon = ({ size = 16 }) =>
  svg(<><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>, size);
export const EditIcon = ({ size = 14 }) =>
  svg(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>, size);
export const TrashIcon = ({ size = 14 }) =>
  svg(<><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></>, size);
export const EyeIcon = ({ size = 15 }) => svg(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>, size);
export const ChevronIcon = ({ up, size = 16 }) => svg(up ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />, size);
export const DownloadIcon = ({ size = 14 }) =>
  svg(<><path d="M4 16v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1" /><polyline points="8 12 12 16 16 12" /><line x1="12" y1="4" x2="12" y2="16" /></>, size);

// ── headers ──────────────────────────────────────────────────────────────────

export function PageHead({ title, sub, action }) {
  return (
    <div className="de-page-head">
      <div>
        <div className="de-page-title">{title}</div>
        {sub ? <div className="de-page-sub">{sub}</div> : null}
      </div>
      {action}
    </div>
  );
}

export function WelcomeBar({ title, sub, icon = '◈' }) {
  return (
    <div className="de-welcome">
      <div className="de-welcome-icon">{icon}</div>
      <div>
        <div className="de-welcome-title">{title || `Welcome back, ${SALON_NAME}`}</div>
        <div className="de-welcome-sub">{sub || 'Here is your customer engagement overview for today.'}</div>
      </div>
    </div>
  );
}

// ── stat cards ───────────────────────────────────────────────────────────────

export function StatCard({ label, value, icon, sub, variant = 'solid', onClick, active }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`de-stat de-stat--${variant}${onClick ? ' de-stat--clickable' : ''}${active ? ' de-stat--active' : ''}`}
    >
      <div className="de-stat-top">
        {icon ? <span className="de-stat-icon">{icon}</span> : null}
        {label}
      </div>
      <div className="de-stat-value">{value}</div>
      {sub ? <div className="de-stat-sub">{sub}</div> : null}
    </Tag>
  );
}

export function StatGrid({ children, style, columns }) {
  return (
    <div className={`de-stat-grid${columns === 4 ? ' de-stat-grid--4' : ''}`} style={style}>
      {children}
    </div>
  );
}

// ── feedback ─────────────────────────────────────────────────────────────────

export function Loader({ text = 'Loading…' }) {
  const message = String(text || 'Loading').replace(/\u2026$/, '').trim() || 'Loading';
  return <PortalSectionFallback message={message} />;
}

export function Spinner({ light }) {
  return <span className={`de-spinner${light ? ' de-spinner--light' : ''}`} aria-hidden />;
}

export function EmptyState({ icon = '🗂', title, body, action }) {
  return (
    <div className="de-empty">
      <div className="de-empty-icon">{icon}</div>
      {title ? <div className="de-empty-title">{title}</div> : null}
      {body ? <div className="de-small">{body}</div> : null}
      {action ? <div className="de-mt-16">{action}</div> : null}
    </div>
  );
}

export function BusyOverlay({ busy, children }) {
  return (
    <div className="de-busy-wrap">
      <div className={`de-busy-content${busy ? ' is-dimmed' : ''}`}>{children}</div>
      {busy ? (
        <div className="de-busy-overlay" aria-busy="true">
          <span className="de-spinner de-spinner--lg" aria-hidden />
        </div>
      ) : null}
    </div>
  );
}

// ── modals ───────────────────────────────────────────────────────────────────

export function Modal({ title, onClose, children, size = '', top = false }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className={`de-modal-overlay${top ? ' de-modal-overlay--top' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}
    >
      <div className={`de-modal${size ? ` de-modal--${size}` : ''} de-fade`}>
        <div className="de-modal-head">
          <div className="de-modal-title">{title}</div>
          <button type="button" className="de-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Confirm({ title = 'Confirm action', message, confirmLabel = 'Confirm', danger = true, busy, onConfirm, onCancel }) {
  return (
    <Modal title={title} onClose={onCancel} size="sm" top>
      <div className="de-muted" style={{ marginBottom: 22, fontSize: 14 }}>
        {message}
      </div>
      <div className="de-row" style={{ justifyContent: 'flex-end', gap: 10 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className={`de-btn ${danger ? 'de-btn--danger' : 'de-btn--primary'}`} onClick={onConfirm} disabled={busy}>
          {busy ? <Spinner /> : null}
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

export function DetailRow({ label, children }) {
  return (
    <div className="de-detail-row">
      <div>{label}</div>
      <div>{children === undefined || children === null || children === '' ? '—' : children}</div>
    </div>
  );
}

// ── filters ──────────────────────────────────────────────────────────────────

export function SearchBox({ value, onChange, placeholder = 'Search…', filled, onEnter, style }) {
  return (
    <label className={`de-search${filled ? ' de-search--filled' : ''}`} style={style}>
      <SearchIcon />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onEnter && onEnter()}
      />
    </label>
  );
}

export function ChipRow({ label, chips, value, onChange, style }) {
  return (
    <div className="de-chip-row" style={style}>
      {label ? <span className="de-chip-label">{label}:</span> : null}
      {chips.map((chip) => (
        <button
          key={chip.key || 'all'}
          type="button"
          className={`de-chip${value === chip.key ? ' active' : ''}`}
          onClick={() => onChange(chip.key)}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

export function ToggleChip({ active, onClick, children }) {
  return (
    <button type="button" className={`de-chip${active ? ' active' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

export function DateRange({ from, to, onFrom, onTo, onClear }) {
  return (
    <div className="de-date-range">
      <span>From</span>
      <input type="date" value={from} onChange={(e) => onFrom(e.target.value)} />
      <span>To</span>
      <input type="date" value={to} onChange={(e) => onTo(e.target.value)} />
      {from || to ? (
        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={onClear}>
          Clear dates
        </button>
      ) : null}
    </div>
  );
}

/** True when an ISO datetime falls inside [from, to] (YYYY-MM-DD, either may be empty). */
export function inDateRange(value, from, to) {
  if (!from && !to) return true;
  const day = String(value || '').slice(0, 10);
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

// ── table helpers ────────────────────────────────────────────────────────────

export const PAGE_SIZES = [10, 25, 50, 100];

export function usePagination(rows, initialSize = 10) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  useEffect(() => {
    setPage(1);
  }, [rows.length, pageSize]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  return { page: safePage, setPage, pageSize, setPageSize, paginated, totalPages, offset: (safePage - 1) * pageSize };
}

function visiblePages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
}

export function Pagination({ page, setPage, pageSize, setPageSize, totalPages, attached }) {
  return (
    <div className={`de-pagination${attached ? ' de-pagination--attached' : ''}`}>
      <div>
        Table Item Qty View
        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <div className="de-page-btns">
        <button type="button" className="de-page-nav" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Previous
        </button>
        {visiblePages(page, totalPages).map((n, i) =>
          n === '…' ? (
            <span key={`gap-${i}`} className="de-muted">
              …
            </span>
          ) : (
            <button key={n} type="button" className={`de-page-num${n === page ? ' active' : ''}`} onClick={() => setPage(n)}>
              {n}
            </button>
          ),
        )}
        <button type="button" className="de-page-nav" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
}

export function EditButton({ onClick, title = 'Edit', disabled }) {
  return (
    <button type="button" className="de-icon-action de-icon-action--edit" onClick={onClick} title={title} aria-label={title} disabled={disabled}>
      <EditIcon />
    </button>
  );
}

export function DeleteButton({ onClick, title = 'Delete', disabled }) {
  return (
    <button type="button" className="de-icon-action de-icon-action--delete" onClick={onClick} title={title} aria-label={title} disabled={disabled}>
      <TrashIcon />
    </button>
  );
}

export function ViewButton({ onClick, title = 'View', disabled }) {
  return (
    <button type="button" className="de-icon-action de-icon-action--view" onClick={onClick} title={title} aria-label={title} disabled={disabled}>
      <EyeIcon />
    </button>
  );
}

// ── channels and statuses ────────────────────────────────────────────────────

export const WHATSAPP_GREEN = '#25D366';
export const VOICE_COLOR = '#6b4c9a';

export const DESK_COLOR = '#9e7436';
export const INSTAGRAM_COLOR = '#d6249f';

/** "@username", or "" when Instagram gave no username (only a numeric id). */
export function instagramHandle(username) {
  const raw = String(username || '').trim().replace(/^@/, '');
  return raw && !/^\d{8,}$/.test(raw) ? `@${raw}` : '';
}

/** How to reach a customer: their phone number, else their Instagram @username. */
export function contactLabel(phone, instagramUsername) {
  if (phone) return formatPhoneLabel(phone);
  return instagramHandle(instagramUsername) || 'Instagram';
}

function formatPhoneLabel(value) {
  const raw = String(value).trim();
  if (raw.startsWith('+')) return raw;
  return raw.length === 10 ? `+91${raw}` : `+${raw}`;
}

export function SourceBadge({ source, showLabel = false, size = 18 }) {
  const voice = source === 'sarvam' || source === 'voice';
  if (source === 'instagram') {
    return (
      <span
        title="Instagram DM"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: INSTAGRAM_COLOR }}
      >
        <FaInstagram size={size - 1} />
        {showLabel ? 'Instagram' : null}
      </span>
    );
  }
  if (source === 'walk_in') {
    return (
      <span
        title="Booked at the desk (walk-in or phone)"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: DESK_COLOR }}
      >
        <FaStore size={size - 3} />
        {showLabel ? 'Walk-in / desk' : null}
      </span>
    );
  }
  if (source === 'checkin') {
    return (
      <span
        title="Salon visit (service lobby check-in)"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: DESK_COLOR }}
      >
        <FaCut size={size - 3} />
        {showLabel ? 'Salon visit' : null}
      </span>
    );
  }
  return (
    <span
      title={voice ? 'Voice call' : 'WhatsApp'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: voice ? VOICE_COLOR : WHATSAPP_GREEN,
      }}
    >
      {voice ? <FaPhoneAlt size={size - 3} /> : <FaWhatsapp size={size} />}
      {showLabel ? (voice ? 'Voice call' : 'WhatsApp') : null}
    </span>
  );
}

export function WhatsAppButton({ onClick, title = 'Open WhatsApp conversation' }) {
  return (
    <button type="button" className="de-icon-action de-icon-action--plain" onClick={onClick} title={title} aria-label={title} style={{ color: WHATSAPP_GREEN }}>
      <FaWhatsapp size={19} />
    </button>
  );
}

export function InstagramButton({ onClick, title = 'Open Instagram conversation' }) {
  return (
    <button type="button" className="de-icon-action de-icon-action--plain" onClick={onClick} title={title} aria-label={title} style={{ color: INSTAGRAM_COLOR }}>
      <FaInstagram size={18} />
    </button>
  );
}

export function VoiceButton({ onClick, title = 'Play voice call recording' }) {
  return (
    <button type="button" className="de-icon-action de-icon-action--plain" onClick={onClick} title={title} aria-label={title} style={{ color: VOICE_COLOR }}>
      <FaPhoneAlt size={16} />
    </button>
  );
}

const APPOINTMENT_STATUS = {
  booked: { label: 'Booked', tone: '' },
  service_taken: { label: 'Service taken', tone: 'ok' },
  no_show: { label: 'No-show', tone: 'muted' },
  not_taken: { label: 'Visited, not taken', tone: 'warn' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
};

export function AppointmentStatus({ status }) {
  const cfg = APPOINTMENT_STATUS[status] || { label: status || '—', tone: 'muted' };
  return <span className={`de-pill${cfg.tone ? ` de-pill--${cfg.tone}` : ''}`}>{cfg.label}</span>;
}

// Conversation status. Booked comes from real bookings; the other three are set by the AI.
export const STAGES = [
  { key: 'booked', label: 'Booked', pill: 'ok', color: 'var(--de-ok)' },
  { key: 'unbooked', label: 'Unbooked', pill: 'warn', color: 'var(--de-warn)' },
  { key: 'enquiry', label: 'Enquiry', pill: 'info', color: 'var(--de-info)' },
  { key: 'irrelevant', label: 'Irrelevant', pill: 'muted', color: '#9a93a3' },
];

/** Chat status when the customer's latest appointment was missed (Conversations only, not a lead stage). */
export const NO_SHOW_STAGE = { key: 'no_show', label: 'No-show', pill: 'danger', color: 'var(--de-danger)' };

/** status: booked | unbooked | enquiry | irrelevant | no_show ('open' from older data shows as Unbooked). */
export function LeadFlag({ status, reason, small }) {
  if (!status) return <span className="de-muted">—</span>;
  const stage = [...STAGES, NO_SHOW_STAGE].find((s) => s.key === (status === 'open' ? 'unbooked' : status)) || STAGES[1];
  return (
    <span className={`de-pill de-pill--${stage.pill}${small ? ' de-pill--sm' : ''}`} title={reason || undefined}>
      {stage.label}
    </span>
  );
}

export function InterventionBadge({ compact }) {
  return <span className={`de-pill de-pill--danger${compact ? ' de-pill--sm' : ''}`}>Human intervention needed</span>;
}

/** Customers who missed 2 or more appointments: staff may want to call and confirm before the next one. */
export function NoShowTag({ count }) {
  if (!count || count < 2) return null;
  return (
    <span className="de-pill de-pill--warn de-pill--sm" title="Missed appointments. Consider calling to confirm the next one.">
      ⚠ {count} no-shows
    </span>
  );
}

function shortWhen(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
  const time = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${day}, ${time}`;
}

/** Their next booking, plus how many more; hover lists them all. Shown beside the chat status. */
export function UpcomingTag({ bookings }) {
  if (!Array.isArray(bookings) || !bookings.length) return null;
  const [next, ...rest] = bookings;
  const all = bookings.map((b) => `${b.service_name} · ${shortWhen(b.starts_at)} · ${b.staff_name || 'any stylist'}`).join('\n');
  return (
    <span className="de-pill de-pill--ok de-pill--sm de-pill--tag" title={`Upcoming bookings:\n${all}`}>
      📅 {next.service_name} · {shortWhen(next.starts_at)}
      {rest.length ? ` +${rest.length} more` : ''}
    </span>
  );
}

/** A service they asked about and have not booked. */
export function InterestTag({ service }) {
  if (!service) return null;
  return (
    <span className="de-pill de-pill--info de-pill--sm de-pill--tag" title="Asked about this and has not booked it">
      💬 Interested: {service}
    </span>
  );
}

export function UnassignedPill() {
  return <span className="de-pill de-pill--warn">Unassigned</span>;
}

// ── export ───────────────────────────────────────────────────────────────────

export function exportCSV(rows, columns, filename) {
  const header = columns.map((c) => `"${c.label}"`).join(',');
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = typeof c.value === 'function' ? c.value(r) : r[c.key] ?? '';
          return `"${String(v).replace(/"/g, '""')}"`;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
